<template>
  <div class="tw-overflow-x-auto tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur">
    <table class="tw-min-w-full tw-border-separate tw-border-spacing-0 tw-text-left">
      <thead>
        <tr class="tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
          <th class="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50/80 tw-backdrop-blur tw-px-3 tw-py-3 tw-cursor-pointer hover:tw-text-slate-900 tw-transition-colors" @click="updateSortBy('orderId')">
            <div class="tw-inline-flex tw-items-center tw-gap-2">
              <span>Order</span>
              <font-awesome-icon 
                :icon="sortBy === 'orderId' && sortOrder === 'ASC' ? 'chevron-up' : 'chevron-down'" 
                class="tw-h-3 tw-w-3 tw-transition-transform"
                :class="sortBy === 'orderId' ? 'tw-text-slate-900' : 'tw-text-slate-300'" 
              />
            </div>
          </th>

          <th class="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50/80 tw-backdrop-blur tw-px-3 tw-py-3 tw-cursor-pointer hover:tw-text-slate-900 tw-transition-colors" @click="updateSortBy('createdOn')">
            <div class="tw-inline-flex tw-items-center tw-gap-2">
              <span>Created</span>
              <font-awesome-icon 
                :icon="sortBy === 'createdOn' && sortOrder === 'ASC' ? 'chevron-up' : 'chevron-down'" 
                class="tw-h-3 tw-w-3"
                :class="sortBy === 'createdOn' ? 'tw-text-slate-900' : 'tw-text-slate-300'" 
              />
            </div>
          </th>

          <th class="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50/80 tw-backdrop-blur tw-px-3 tw-py-3">Type</th>

          <th class="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50/80 tw-backdrop-blur tw-px-3 tw-py-3 tw-cursor-pointer hover:tw-text-slate-900 tw-transition-colors" @click="updateSortBy('receiver')">
            <div class="tw-inline-flex tw-items-center tw-gap-2">
              <span>Receiver</span>
              <font-awesome-icon 
                :icon="sortBy === 'receiver' && sortOrder === 'ASC' ? 'chevron-up' : 'chevron-down'" 
                class="tw-h-3 tw-w-3"
                :class="sortBy === 'receiver' ? 'tw-text-slate-900' : 'tw-text-slate-300'" 
              />
            </div>
          </th>

          <th class="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50/80 tw-backdrop-blur tw-px-3 tw-py-3 tw-cursor-pointer hover:tw-text-slate-900 tw-transition-colors" @click="updateSortBy('billing')">
            <div class="tw-inline-flex tw-items-center tw-gap-2">
              <span>Bill To</span>
              <font-awesome-icon 
                :icon="sortBy === 'billing' && sortOrder === 'ASC' ? 'chevron-up' : 'chevron-down'" 
                class="tw-h-3 tw-w-3"
                :class="sortBy === 'billing' ? 'tw-text-slate-900' : 'tw-text-slate-300'" 
              />
            </div>
          </th>

          <th class="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50/80 tw-backdrop-blur tw-px-3 tw-py-3 tw-cursor-pointer hover:tw-text-slate-900 tw-transition-colors" @click="updateSortBy('status')">
            <div class="tw-inline-flex tw-items-center tw-gap-2">
              <span>Status</span>
              <font-awesome-icon 
                :icon="sortBy === 'status' && sortOrder === 'ASC' ? 'chevron-up' : 'chevron-down'" 
                class="tw-h-3 tw-w-3"
                :class="sortBy === 'status' ? 'tw-text-slate-900' : 'tw-text-slate-300'" 
              />
            </div>
          </th>

          <th class="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50/80 tw-backdrop-blur tw-px-3 tw-py-3 tw-cursor-pointer hover:tw-text-slate-900 tw-transition-colors" @click="updateSortBy('bl')">
            <div class="tw-inline-flex tw-items-center tw-gap-2">
              <span>BL</span>
              <font-awesome-icon 
                :icon="sortBy === 'bl' && sortOrder === 'ASC' ? 'chevron-up' : 'chevron-down'" 
                class="tw-h-3 tw-w-3"
                :class="sortBy === 'bl' ? 'tw-text-slate-900' : 'tw-text-slate-300'" 
              />
            </div>
          </th>

          <th class="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50/80 tw-backdrop-blur tw-px-3 tw-py-3">Containers</th>
        </tr>
      </thead>
      <tbody class="tw-divide-y tw-divide-slate-200/70">
        <tr
          v-for="(order, orderIdx) in orders"
          :key="order.id || orderIdx"
          class="order-item-row tw-cursor-pointer tw-transition-colors"
          :class="[
            getIsLastOpenedListItem(lastOpenedOrderId, order.id)
              ? 'tw-bg-amber-100/70'
              : (Number(orderIdx) % 2 === 0 ? 'tw-bg-white/40' : 'tw-bg-slate-50/40'),
            'hover:tw-bg-indigo-50/80',
          ]"
          v-on:click="selectOrder(order.id)"
        >
          <td class="tw-whitespace-nowrap tw-px-3 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-900">
            <CopyButton
              :value="String(order.id)"
              mode="wrap-content"
              title="Copy order ID"
              aria-label="Copy order ID to clipboard"
            >
              {{ order.id }}
            </CopyButton>
          </td>
          <td class="tw-whitespace-nowrap tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">{{ order.createdAt }}</td>
          <td class="tw-px-3 tw-py-3 tw-text-sm">
            <span
              class="tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold"
              :class="isOceanExport(order)
                ? 'tw-bg-amber-100 tw-text-amber-900'
                : (isOceanImport(order) || isDropPick(order))
                  ? 'tw-bg-sky-100 tw-text-sky-900'
                  : isLandOrder(order)
                    ? 'tw-bg-emerald-100 tw-text-emerald-900'
                    : 'tw-bg-slate-100 tw-text-slate-700'"
            >
              {{ order.orderType }}
            </span>
          </td>
          <td class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">
            <span v-if="order.customer" class="tw-font-medium">{{ order.customer.name }}</span>
            <span v-else-if="order.receiver">{{ order.receiver.name }}</span>
            <span v-else class="tw-text-slate-400">—</span>
          </td>
          <td class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">
            <span v-if="order.billTo">{{ order.billTo.name }}</span>
            <span v-else class="tw-text-slate-400">—</span>
          </td>
          <td class="tw-px-3 tw-py-3 tw-text-sm">
            <span
              v-if="!order.status"
              class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-amber-100 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-amber-900"
            >
              Unknown
            </span>
            <span
              v-else-if="order.status === 'Not Available'"
              class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-rose-100 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-rose-900"
            >
              Not Available
            </span>
            <span
              v-else-if="order.status === 'In Progress'"
              class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-emerald-100 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-emerald-900"
            >
              {{ order.status }}
            </span>
            <span
              v-else-if="order.status === 'Cancelled'"
              class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-red-400 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-white"
            >
              {{ order.status }}
            </span>
            <span
              v-else
              class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-slate-100 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-slate-800"
            >
              {{ order.status }}
            </span>
          </td>
          <td class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">
            <span v-if="order.isExport" class="tw-font-mono tw-text-xs">
              <CopyButton
                :value="order.bookingNumber"
                mode="wrap-content"
                title="Copy booking number"
                aria-label="Copy booking number to clipboard"
              >
                {{ order.bookingNumber }}
              </CopyButton>
            </span>
            <span v-else-if="order.billOfLading" class="tw-font-mono tw-text-xs">
              <CopyButton
                :value="order.billOfLading.billOfLadingId"
                mode="wrap-content"
                title="Copy B/L ID"
                aria-label="Copy B/L ID to clipboard"
              >
                {{ order.billOfLading.billOfLadingId }}
              </CopyButton>
            </span>
            <span v-else class="tw-text-slate-400">N/A</span>
          </td>
          <td class="tw-whitespace-nowrap tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">
            <div class="tw-flex tw-items-center tw-gap-1">
              <font-awesome-icon icon="box" class="tw-text-slate-400 tw-h-3" />
              {{ order.containers ? order.containers.length : 0 }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { orderTypeMethodsMixin } from '../../utils/order';
import { Order } from '../../types';
import CopyButton from '../core/CopyButton.vue';
import { getIsLastOpenedListItem } from '../../utils/savedLastOpenedListItem';

export default defineComponent({
  name: 'OrderTable',
  components: {
    CopyButton,
  },
  props: {
    orders: {
      type: Array as PropType<(Order & { createdAt?: string; customer?: { name: string } })[]>,
      default: () => []
    },
    sortBy: {
      type: String,
      default: 'createdOn'
    },
    sortOrder: {
      type: String,
      default: 'DESC'
    },
    lastOpenedOrderId: {
      type: String,
      default: null
    }
  },
  mixins: [orderTypeMethodsMixin],

  methods: {
    getIsLastOpenedListItem,
    selectOrder(id: number | string) {
      this.$emit('selectOrder', id);
    },
    updateSortBy(val: string) {
      this.$emit('updateSortBy', val);
    },
  },
});
</script>

<style lang="scss">
/* make table rows mouse cursor "zoom in" to imply "click to view details" */
table tbody tr.order-item-row td {
  cursor: zoom-in;
}
</style>
